import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import TeamAccessManager from "../components/TeamAccessManager";
import { FloppyDisk, Lock, Sparkle, Megaphone, Barbell, FilePdf, DownloadSimple } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const EMPTY = {
  company_name: "",
  role: "",
  context: "",
  title: "",
  years_experience: "",
  location: "",
  disciplines: "",
  belts: "",
  certifications: "",
  philosophy: "",
  tone_directives: "",
  signature_block: "",
};

// Brouillon proposé (basé sur le profil : Kru, 25 ans, ton direct/ferme, pas de flatterie).
// Ne remplit QUE les champs vides — l'utilisateur ajuste ensuite.
const DRAFT = {
  company_name: "Institut Impact Élite Martial",
  role: "Fondateur · Kru",
  title: "Kru, Officiel certifié",
  location: "Val-d'Or, QC",
  years_experience: "25",
  disciplines: "Kickboxing, K-1, Muay Thaï, Jiu-Jitsu brésilien, Jiu-Jitsu japonais Can Ryu",
  belts: "BJJ, Jiu-Jitsu Japonais Can Ryu, Muay Thaï (3 ceintures noires)",
  certifications: "Officiel certifié Lumpinee Stadium · Officiel Muay Thai Canada",
  philosophy:
    "Je vends la discipline, le respect et la qualité de vie à long terme — pas un abonnement de gym. " +
    "L'entraînement martial est un investissement santé et mental qui rapporte toute la vie. " +
    "Je privilégie la constance sur la motivation, et le travail bien fait sur la performance rapide. " +
    "Règle 80/20 : la présence régulière et les fondamentaux donnent l'essentiel des résultats. " +
    "Chaque membre est traité comme un athlète en devenir, peu importe son niveau. " +
    "Jamais de vente sous pression : on explique la valeur, le client décide.",
  tone_directives:
    "Direct, ferme, professionnel. Phrases courtes et claires. Tutoiement. " +
    "Aucune formule promotionnelle ni superlatif marketing. Pas de flatterie, pas de « j'espère que vous allez bien ». " +
    "Pas d'emoji. Va droit au but : contexte, action attendue, échéance. Respectueux mais sans détour. Français québécois soigné.",
  signature_block:
    "Sensei-Kru Ralph David Jean-Pierre\nPDG — Institut Impact Élite Martial\nVal-d'Or, QC",
  context:
    "Institut Impact Élite Martial (Val-d'Or, QC). 25 ans d'expérience ; officiel certifié Lumpinee Stadium et Muay Thai Canada. " +
    "Disciplines : Kickboxing, K-1, Muay Thaï, Jiu-Jitsu brésilien, Jiu-Jitsu japonais Can Ryu. " +
    "Programmes : PCV/PCN 12 sem (3x/sem), Master Club, Programme compétitif Muay Thaï Junior 24 sem, Petits Samouraïs, PowerKick Her. " +
    "Rabais familial pour le 3e membre. Événements : Jeux Martiaux Inter-Écoles, Gala, BBQ annuel. " +
    "Priorités : rétention des membres, réduction des impayés, croissance des inscriptions au dojo. " +
    "Plan de redressement financier en cours — surveiller la trésorerie et les paiements Stripe.",
};

export default function Profile() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => {
      const p = r.data || {};
      setForm({ ...EMPTY, ...p, years_experience: p.years_experience ?? "" });
    }).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Pré-remplit UNIQUEMENT les champs vides avec le brouillon proposé.
  const proposeDraft = () => {
    setForm((f) => {
      const next = { ...f };
      let filled = 0;
      Object.keys(DRAFT).forEach((k) => {
        const cur = (next[k] ?? "").toString().trim();
        if (!cur) { next[k] = DRAFT[k]; filled += 1; }
      });
      if (filled === 0) toast.info("Tous les champs sont déjà remplis — rien à proposer.");
      else toast.success(`${filled} champ(s) pré-rempli(s)`, { description: "Ajuste le texte à ton goût, puis clique Enregistrer." });
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      payload.years_experience = payload.years_experience === "" ? null : Number(payload.years_experience);
      await api.put("/profile", payload);
      toast.success("Profil sauvegardé — l'IA en tient compte immédiatement");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Ton identité, ton ton, ta voix"
          icon={Sparkle}
          breadcrumbs={[{ label: "IA, système & support" }, { label: "Profil" }]}
          subtitle="Tout ce que tu remplis ici est injecté automatiquement dans chaque génération IA — courriels, marketing, stratégies financières, chat stratégique. L'app parle en ton nom."
        />
      </div>

      <div className="px-8 lg:px-12 py-8 max-w-5xl space-y-8">
        <div className="hard-border p-5 bg-secondary/30 flex items-start gap-3">
          <Lock size={20} className="text-[#D90429] mt-0.5 shrink-0"/>
          <div className="text-sm">
            <strong>Données protégées.</strong> Ces informations restent strictement sur ton compte. Elles servent uniquement à ce que l'IA réponde en respectant ton identité, ton expertise et ton ton.
          </div>
        </div>

        {(user?.is_admin || user?.role === "admin") && <TeamAccessManager />}

        {/* Brouillon IA — pré-remplissage assisté */}
        <div className="hard-border p-5 bg-secondary/30 flex flex-wrap items-center justify-between gap-3" data-testid="profile-draft-toolbar">
          <div className="flex items-center gap-3">
            <Sparkle size={20} className="text-[#D90429] shrink-0" />
            <div className="text-sm">
              <strong>Besoin d'un point de départ ?</strong> Je peux pré-remplir les champs vides (philosophie, ton, contexte)
              d'après ton profil de Kru. Tu ajustes ensuite avant d'enregistrer.
            </div>
          </div>
          <button onClick={proposeDraft} data-testid="profile-propose-draft"
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 px-5 py-2.5 ticker flex items-center gap-2 transition-colors shrink-0">
            <Sparkle size={16} /> Proposer un brouillon
          </button>
        </div>

        {/* Documents & Exports */}
        <div className="hard-border" data-testid="profile-exports-section">
          <div className="px-5 py-4 bg-foreground text-white flex items-center gap-2">
            <FilePdf size={18}/>
            <div className="ticker">Documents & exports</div>
          </div>
          <div className="p-5 space-y-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/call-recap.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-download-call-recap-pdf"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const res = await api.get("/exports/call-recap.pdf", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "Recap_Mon_Assistant_Pro_Pour_Appel_Emergent.pdf";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("PDF récap téléchargé");
                } catch {
                  toast.error("Téléchargement impossible");
                }
              }}
              className="flex items-center justify-between gap-3 hard-border p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <FilePdf size={28} className="text-[#D90429] shrink-0"/>
                <div>
                  <div className="font-semibold">Récap pour appel Emergent</div>
                  <div className="text-xs text-muted-foreground mt-0.5">État complet de l'app + 4 intégrations à finaliser (Gmail, Meta, QuickBooks, WellyBox) + questions précises à poser</div>
                </div>
              </div>
              <DownloadSimple size={20} className="text-muted-foreground shrink-0"/>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/meta-review-guide.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-download-meta-guide-pdf"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const res = await api.get("/exports/meta-review-guide.pdf", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "Guide_Meta_App_Review.pdf";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Guide Meta App Review téléchargé");
                } catch {
                  toast.error("Téléchargement impossible");
                }
              }}
              className="flex items-center justify-between gap-3 hard-border p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <FilePdf size={28} className="text-[#1877F2] shrink-0"/>
                <div>
                  <div className="font-semibold">Guide Meta App Review (FB + IG)</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Tous les textes prêts à coller (en anglais) + script vidéo screencast 60 sec + checklist pré-soumission. Pour soumettre en solo.</div>
                </div>
              </div>
              <DownloadSimple size={20} className="text-muted-foreground shrink-0"/>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/pochette-billy.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-download-pochette-billy"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const res = await api.get("/exports/pochette-billy.pdf", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "Pochette_Comptable_Billy_Breton.pdf";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Pochette Billy téléchargée");
                } catch {
                  toast.error("Téléchargement impossible");
                }
              }}
              className="flex items-center justify-between gap-3 hard-border p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <FilePdf size={28} className="text-emerald-500 shrink-0"/>
                <div>
                  <div className="font-semibold">Pochette comptable Billy Breton</div>
                  <div className="text-xs text-muted-foreground mt-0.5">CA par année + ventilation catégories + dépenses Toyota/Nissan/Assurances/Hydro classées. Prête pour ta prochaine rencontre.</div>
                </div>
              </div>
              <DownloadSimple size={20} className="text-muted-foreground shrink-0"/>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/comparatif-zen-planner.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-download-comparatif-zp"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const res = await api.get("/exports/comparatif-zen-planner.pdf", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "Comparatif_ZenPlanner_vs_MonAssistantPro.pdf";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Comparatif téléchargé");
                } catch {
                  toast.error("Téléchargement impossible");
                }
              }}
              className="flex items-center justify-between gap-3 hard-border p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <FilePdf size={28} className="text-amber-500 shrink-0"/>
                <div>
                  <div className="font-semibold">Comparatif Zen Planner vs Mon Assistant Pro</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Analyse fonctionnelle + tarifs 2026 + plan migration 4-6 sem. Économies estimées 2 800 - 5 200 $ USD/an.</div>
                </div>
              </div>
              <DownloadSimple size={20} className="text-muted-foreground shrink-0"/>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/ecosysteme-apps.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-download-ecosysteme-apps"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const res = await api.get("/exports/ecosysteme-apps.pdf", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "Analyse_Ecosysteme_Apps.pdf";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Analyse apps téléchargée");
                } catch {
                  toast.error("Téléchargement impossible");
                }
              }}
              className="flex items-center justify-between gap-3 hard-border p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <FilePdf size={28} className="text-sky-500 shrink-0"/>
                <div>
                  <div className="font-semibold">Ménage de tes applications</div>
                  <div className="text-xs text-muted-foreground mt-0.5">~25 apps remplaçables. Économies 480-720 $ USD/mois après migration complète. Plan 3 priorités (immédiat / progressif / à garder).</div>
                </div>
              </div>
              <DownloadSimple size={20} className="text-muted-foreground shrink-0"/>
            </a>
          </div>
        </div>

        {/* Compte */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 hard-border">
          <div className="p-5">
            <div className="ticker text-muted-foreground">Nom complet</div>
            <div className="font-mono font-semibold mt-1" data-testid="profile-name">{user?.full_name}</div>
          </div>
          <div className="p-5 md:border-l border-border">
            <div className="ticker text-muted-foreground">Courriel</div>
            <div className="font-mono font-semibold mt-1" data-testid="profile-email">{user?.email}</div>
          </div>
        </div>

        {/* Section 1 — Identité professionnelle */}
        <section className="hard-border">
          <div className="px-5 py-4 bg-foreground text-white flex items-center gap-2">
            <Barbell size={18}/>
            <div className="ticker">01 · Identité & expertise</div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Entreprise" testid="profile-company"
              value={form.company_name} onChange={(v) => update("company_name", v)}
              placeholder="Institut Impact Élite Martial"/>
            <Field label="Rôle" testid="profile-role"
              value={form.role} onChange={(v) => update("role", v)}
              placeholder="Fondateur · Kru"/>
            <Field label="Titre / fonction (court)" testid="profile-title"
              value={form.title} onChange={(v) => update("title", v)}
              placeholder="Kru, Officiel certifié"/>
            <Field label="Localisation" testid="profile-location"
              value={form.location} onChange={(v) => update("location", v)}
              placeholder="Val-d'Or, QC"/>
            <Field label="Années d'expérience" testid="profile-years" type="number"
              value={form.years_experience} onChange={(v) => update("years_experience", v)}
              placeholder="25"/>
            <Field label="Disciplines enseignées" testid="profile-disciplines"
              value={form.disciplines} onChange={(v) => update("disciplines", v)}
              placeholder="Kickboxing, K-1, Muay Thaï"/>
            <Field label="Ceintures / grades" testid="profile-belts" full
              value={form.belts} onChange={(v) => update("belts", v)}
              placeholder="BJJ, Jiu-Jitsu Japonais Can Ryu, Muay Thaï (3 ceintures noires)"/>
            <Field label="Certifications & sanctions" testid="profile-certs" full
              value={form.certifications} onChange={(v) => update("certifications", v)}
              placeholder="Officiel certifié Lumpinee Stadium · Officiel Muay Thai Canada"/>
          </div>
        </section>

        {/* Section 2 — Philosophie & ton IA */}
        <section className="hard-border">
          <div className="px-5 py-4 bg-foreground text-white flex items-center gap-2">
            <Sparkle size={18}/>
            <div className="ticker">02 · Philosophie & ton de l'IA</div>
          </div>
          <div className="p-5 space-y-4">
            <Textarea label="Philosophie / approche commerciale" testid="profile-philosophy"
              rows={5} value={form.philosophy} onChange={(v) => update("philosophy", v)}
              placeholder="Ex: Vendre la discipline et la qualité de vie à long terme. Investissement santé > dépenses superficielles. Règle d'or 80/20..."/>
            <Textarea label="Directives de ton (l'IA suit ça à la lettre)" testid="profile-tone"
              rows={4} value={form.tone_directives} onChange={(v) => update("tone_directives", v)}
              placeholder="Ex: Direct, ferme, professionnel. Phrases courtes. AUCUNE formule promotionnelle. Pas de flatterie. Pas d'emoji."/>
            <Textarea label="Signature courriel (utilisée pour les drafts)" testid="profile-signature"
              rows={4} value={form.signature_block} onChange={(v) => update("signature_block", v)}
              placeholder="Ralph Gassonnette Jean-Pierre&#10;Kru — Institut Impact Élite Martial&#10;Val-d'Or, QC"/>
          </div>
        </section>

        {/* Section 3 — Contexte additionnel libre */}
        <section className="hard-border">
          <div className="px-5 py-4 bg-foreground text-white flex items-center gap-2">
            <Megaphone size={18}/>
            <div className="ticker">03 · Contexte additionnel (notes libres)</div>
          </div>
          <div className="p-5">
            <Textarea label="Tout ce que l'IA doit savoir : situation, priorités, contraintes, échéances" testid="profile-context"
              rows={10} value={form.context} onChange={(v) => update("context", v)}
              placeholder="Ex: Gala 16 mai 2026. Programme compétitif Muay Thaï Junior 24 sem. Rabais familial -10% 3e membre. Plan de redressement financier en cours..."/>
            <div className="ticker text-muted-foreground mt-2">{form.context.length} caractères</div>
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button onClick={save} disabled={busy} data-testid="profile-save"
            className="bg-[#D90429] hover:bg-[#BF0424] text-white px-6 py-3 ticker flex items-center gap-2 disabled:opacity-60 transition-colors">
            <FloppyDisk size={16}/> {busy ? "Sauvegarde…" : "Enregistrer"}
          </button>
          <span className="ticker text-muted-foreground">Modifications appliquées immédiatement à toutes les générations IA</span>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, type = "text", full = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="ticker text-muted-foreground block mb-2">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full hard-border px-4 py-2.5" placeholder={placeholder}/>
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder, testid, rows = 4 }) {
  return (
    <div>
      <label className="ticker text-muted-foreground block mb-2">{label}</label>
      <textarea data-testid={testid} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full hard-border px-4 py-3 text-sm resize-y" placeholder={placeholder}/>
    </div>
  );
}
